<template>
  <div class="path-picker">
    <div class="dropdown" :class="{ 'is-active': isOpen, 'is-up': isUp }">
      <div class="dropdown-trigger" @click="togglePicker" v-click-away="closePicker">
        <button class="button" :class="{ fixed: fixed }">
          <span class="selected-path">{{ description }}</span>
          <span class="icon" v-if="!fixed">
            <font-awesome-icon icon="angle-down"></font-awesome-icon>
          </span>
        </button>
      </div>
      <div class="dropdown-menu" id="dropdown-menu" role="menu">
        <div class="dropdown-content">
          <a @click="choosePath(path.id)" class="dropdown-item" v-for="path in pathsToChooseFrom" :key="path.id">
            {{ path.name }}
          </a>
          <hr class="dropdown-divider" />
          <a @click="addNewPath" class="dropdown-item"> Add new path </a>
        </div>
      </div>
    </div>
    <div class="goto-button">
      <button class="button" @click="goToPath(selected)" :disabled="!hasSelectedPath(selected)">
        <span class="icon">
          <font-awesome-icon icon="arrow-right"></font-awesome-icon>
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { nanoid } from 'nanoid';
import { dropDownHelper } from '@/shared/components/dropDownMenu/dropDownHelper';
import { PathViewModel } from '@/features/bots/models/designer';
import { ADD_PATH, SET_CURRENT_PATH } from '@/store/bot-designer/paths/mutationTypes';

const props = defineProps<{ value: string; fixed?: boolean }>();
const emit = defineEmits(['path-selected']);
const store = useStore();

const paths = computed<PathViewModel[]>(() => store.getters.paths);
const currentPathId = computed<string | undefined>(() => store.getters.currentPathId);
const selected = ref(props.value);
const isOpen = ref(false);
const isUp = ref(false);

watch(
  () => props.value,
  (newValue) => {
    selected.value = newValue;
  },
  { immediate: true },
);

const pathsToChooseFrom = computed(() => {
  return paths.value.filter((p) => p.id !== currentPathId.value);
});

const description = computed(() => {
  if (!selected.value) return 'Choose a path...';
  const selectedPath = paths.value.find((p) => p.id === selected.value);
  if (!selectedPath) {
    selected.value = '';
    emit('path-selected', selected.value);
    return 'Choose a path...';
  }
  return selectedPath.name;
});

const choosePath = (pathId: string) => {
  selected.value = pathId;
  emit('path-selected', selected.value);
};

const addNewPath = () => {
  const newPath: PathViewModel = {
    id: `path-${nanoid(7)}`,
    name: 'New Path',
    states: [],
  };
  store.commit(ADD_PATH, newPath);
  selected.value = newPath.id;
  emit('path-selected', selected.value);
};

const closePicker = () => {
  isOpen.value = false;
  isUp.value = false;
};

const togglePicker = () => {
  if (props.fixed) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setTimeout(() => {
      isUp.value = dropDownHelper.isDropDownUp();
    }, 0);
  }
};

const hasSelectedPath = (pathId: string) => {
  return pathId && pathId.length > 0;
};

const goToPath = (pathId: string) => {
  store.commit(SET_CURRENT_PATH, pathId);
};

const handleClickOutside = (event: Event) => {
  if (!event.target || !(event.target as HTMLElement).closest('.dropdown')) {
    closePicker();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
.path-picker {
  display: flex;
  flex: 1;
  border-radius: 5px;
}

.dropdown {
  display: flex;
  flex: 1;
  background-color: $color-netcall-jonquil;
  width: 150px;

  .dropdown-trigger {
    overflow: hidden;

    .selected-path {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.dropdown-content {
  max-height: 300px;
  overflow: auto;
}

.goto-button {
  display: flex;
  align-items: center;
  background-color: $color-netcall-deep-sea;
  color: $color-netcall-jonquil;

  &:hover {
    cursor: pointer;
    background-color: $color-netcall-jonquil;
    color: $color-netcall-deep-sea;
  }

  .button[disabled] {
    color: white;
    background-color: #d4d9e3;
    opacity: 0.9;
  }
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

.button {
  background-color: transparent;
  border: none;
  border-radius: 0px;
  color: inherit;
  width: 100%;

  &.fixed {
    cursor: default;

    &:focus:not(:active) {
      box-shadow: none;
    }
  }

  &:focus {
    box-shadow: none;
  }
}
</style>
